<script>
// Module-scoped, not inside <script setup> — this panel remounts (and so
// would lose any per-instance state) every time it's reopened, even when
// v/c/the target distances haven't changed since the last time. findAngle's
// result depends only on (v, c, d, cut_angle) — it seeds its own search
// (defaulting to estimateAngle) rather than needing a guess threaded in —
// so those four are the whole cache key. A thrown/failed search is never
// cached (the wrapped call below only runs on a miss and only stores on a
// normal return), so a cache hit always reflects a previously-validated
// answer, never a stale attempt that didn't converge.
import * as calc from '../calc.js';

const findAngleCache = new Map();
const FIND_ANGLE_CACHE_LIMIT = 20000; // guards against unbounded growth over a long session; just a cache, safe to drop

function cachedFindAngle(v, c, d, cut_angle) {
  const key = `${v}|${c}|${d}|${cut_angle}`;
  const cached = findAngleCache.get(key);
  if (cached !== undefined) return cached;

  const a = calc.findAngle(v, c, d, cut_angle);

  if (findAngleCache.size >= FIND_ANGLE_CACHE_LIMIT) findAngleCache.clear();
  findAngleCache.set(key, a);
  return a;
}
</script>

<script setup>
import { watch, ref, nextTick, onMounted, onBeforeUnmount } from 'vue';
import { useShared } from '../state.js';
import { useTheme } from '../use_theme.js';
import { usePersisted } from '../use_persisted.js';
import { scopedKey, useSetups } from '../setups.js';
import { useToasts } from '../use_toasts.js';
import { useDeferredCompute } from '../use_deferred_compute.js';
import { yieldToFrame } from '../async_chunking.js';
import RecomputeButton from './recompute_button.vue';
import UnitSelect from './unit_select.vue';

const { inputs, computedResult, isStale, recompute } = useShared();
const { pushToast } = useToasts();
const { activeSetupId } = useSetups();

let lastTrajectoryError = null;
let lastTrajectoryDistanceErrors = new Map(); // distance -> { message, at } last toasted for it

// Re-toast a persisting failure after this long, rather than suppressing it
// for the rest of the session — a one-off 5s toast is easy to miss (e.g.
// you're mid-edit on a different field when it appears), and if it's
// missed there was previously no way to ever find out that distance
// wasn't showing.
const DISTANCE_ERROR_RETOAST_MS = 8000;

function maybeToastDistanceError(d, unit, message) {
  const now = Date.now();
  const prev = lastTrajectoryDistanceErrors.get(d);
  if (prev && prev.message === message && now - prev.at < DISTANCE_ERROR_RETOAST_MS) {
    return;
  }
  lastTrajectoryDistanceErrors.set(d, { message, at: now });
  pushToast(`Trajectory: couldn't calculate ${d}${unit} — ${message}`);
}

// Scoped to the active setup — see mark.vue for why.
const input = usePersisted(scopedKey('trajectory-inputs', activeSetupId.value), {
  unit: 'm',
  cut_angle: 0,
  distances: [18, 30, 70],
}, {
  // Older saved state stored distances as a comma-separated string —
  // fall back to defaults rather than choke on the old shape.
  validate: (v) => Array.isArray(v?.distances),
});

function addDistance() {
  const last = input.value.distances[input.value.distances.length - 1];
  input.value.distances.push(Number.isFinite(last) ? Math.round(last + 10) : 10);
}

function removeDistance(index) {
  if (input.value.distances.length > 1) {
    input.value.distances.splice(index, 1);
  }
}

const myCanvas = ref(null);
const canvasContainer = ref(null);

// ─── Sizing constants ───
const MAX_HEIGHT_RATIO = 0.8;   // cap height at 80% of viewport
const MIN_WIDTH = 200;          // don't let width go below this
const BOW_WIDTH = 5;            // width of the bow marker box
const TITLE_HEIGHT = 28;        // reserved band at the top for the canvas title
const AXIS_LABEL_HEIGHT = 20;   // reserved band at the bottom for x-axis tick labels
const AXIS_LABEL_PAD = 6;       // gap between the plot area and the x-axis tick labels

// ─── Nice tick step (1/2/5 × 10^n), sized so labels don't crowd each other ───
function niceStep(range, pxAvailable, pxPerLabel) {
  if (!(range > 0) || pxAvailable <= 0) return range > 0 ? range : 1;
  const targetCount = Math.max(1, Math.floor(pxAvailable / pxPerLabel));
  const rawStep = range / targetCount;
  const mag = Math.pow(10, Math.floor(Math.log10(rawStep)));
  const norm = rawStep / mag;
  const nice = norm <= 1 ? 1 : norm <= 2 ? 2 : norm <= 5 ? 5 : 10;
  return nice * mag;
}

function formatTick(v, step) {
  return step >= 1 ? v.toFixed(0) : v.toFixed(2);
}

function getCanvasColors() {
  const styles = getComputedStyle(document.documentElement);
  return {
    bg:     styles.getPropertyValue('--canvas-bg').trim(),
    grid:   styles.getPropertyValue('--canvas-grid').trim(),
    line:   styles.getPropertyValue('--canvas-line').trim(),
    text:   styles.getPropertyValue('--canvas-text').trim(),
    accent: styles.getPropertyValue('--canvas-accent').trim(),
  };
}

function drawTrajectory(ctx, x_scale, y_scale, launch_x, launch_y, unit, h, v,
  c, d, cut, plotBottom) {
  let dd = d * calc.toScale(unit);
  let { x: x_target, y: y_target } = calc.cutDists(dd, cut);

  ctx.beginPath();
  ctx.moveTo(launch_x, launch_y);

  let f = function (p) {
    let x = p[0];
    let y = p[1];
    ctx.lineTo(launch_x + x * x_scale, launch_y - y * y_scale);
    return true;
  }

  let a = cachedFindAngle(v, c, dd, cut);

  // calc()'s adaptive step can grow large enough on a smooth arc to plot
  // only a dozen or so points across the whole shot — accurate at each
  // point, but visibly faceted once connected with straight lineTo
  // segments (worst near the apex, where curvature is highest). Capping
  // the step here (rendering only — every solver call elsewhere leaves
  // this uncapped) trades a few extra RK4 steps, once per draw, for a
  // curve that actually looks like one.
  const vx = v * Math.cos(a);
  const maxTStep = vx > 0 ? x_target / vx / 60 : Infinity;

  calc.calc(v, c, a, x_target, f, maxTStep);

  ctx.stroke();

  const boxX = launch_x + x_target * x_scale;
  const boxTop = launch_y - h/2 - y_target * y_scale;

  const target = new Path2D();
  target.rect(boxX, boxTop, 5, h);

  ctx.stroke(target);

  // ─── Distance label: normally below the target box, but a cut angle can
  // push the box down near the x-axis label band — flip it above instead
  // of letting the two overlap ───
  const belowY = boxTop + h + 5;
  if (belowY + 14 > plotBottom) {
    ctx.textBaseline = "bottom";
    ctx.fillText(d + unit, boxX, boxTop - 5);
    ctx.textBaseline = "top";
  } else {
    ctx.fillText(d + unit, boxX, belowY);
  }
}

// Sizes the canvas and works out everything drawContent() below needs to
// actually draw — split out from it so the (comparatively cheap, a
// couple of cached calc.maxHeight calls) sizing can run immediately on
// mount, ahead of the panel's own open animation measuring the
// container's height, while the drawing itself — which repeats the
// expensive part, findAngle's iterative solver, once per distance — can
// still be deferred/chunked separately below without leaving the
// container sized wrong (a bare <canvas>'s default) in the meantime.
async function sizeCanvas() {
  if (!myCanvas.value || !canvasContainer.value) {
    return null;
  }

  var dists = [];
  var cut = 0;
  let unit = input.value.unit;

  try {
    dists = input.value.distances
      .filter(d => Number.isFinite(d) && d > 0)
      .sort((a, b) => a - b);

    // Past ~±89° the shot is effectively vertical — calc()'s physics
    // solver divides by horizontal velocity, which heads to zero right
    // around there. Reject before it reaches the solver instead of
    // surfacing whatever cryptic failure falls out of that division.
    if (input.value.cut_angle < -89 || input.value.cut_angle > 89) {
      throw new Error(`Cut angle must be between -89° and 89° (got ${input.value.cut_angle}°)`);
    }

    // This app's Cut angle convention is the opposite of calc.js's
    // cut_angle: a positive value here means the target is BELOW the
    // shooter (downhill), negative means above (uphill) — see
    // cut_angle_camera.vue's own comment on this (mark.vue's cut field
    // uses the same convention). Negating here converts it to calc.js's
    // convention instead (positive cut_angle = target above/uphill, per
    // cutDists' y_target = sin(cut_angle) * d).
    cut = input.value.cut_angle * -calc.radian;
    lastTrajectoryError = null;
  } catch (error) {
    console.log('error drawing trajectory, invalid options:', error.message);
    if (error.message !== lastTrajectoryError) {
      lastTrajectoryError = error.message;
      pushToast(`Trajectory: ${error.message}`);
    }
    return null;
  }

  if (isStale.value) {
    await recompute();
  }

  let r = computedResult.value;

  var max_x = 0;
  var max_y = 0;
  var min_y = 0;

  for (let i = dists.length - 1; i >= 0; i--) {
    try {
      let x = dists[i] * calc.toScale(unit);
      max_y = calc.maxHeight(r.v, r.drag, x, cut);
      let { x: x_target, y: y_target } = calc.cutDists(x, cut);
      max_x = x_target;
      min_y = Math.min(0, y_target);
      break;
    } catch (error) {
      console.log("Failed to get max height for " + dists[i] + " : " + error);
    }
  }

  // ─── Y tick bounds, computed up front: the top/bottom tick labels round
  // out to the next nice number, which can sit past max_y/min_y — size the
  // canvas from those rounded bounds or the top/bottom label gets clipped ───
  let labelMinY = Math.floor(min_y / calc.toScale(unit));
  let labelMaxY = Math.ceil(max_y / calc.toScale(unit));
  let sizeMinY = Math.min(min_y, labelMinY * calc.toScale(unit));
  let sizeMaxY = Math.max(max_y, labelMaxY * calc.toScale(unit));

  // ─── Measure available width from the container's content area ───
  const containerEl = canvasContainer.value;
  const containerStyle = getComputedStyle(containerEl);
  const padLeft = parseFloat(containerStyle.paddingLeft) || 0;
  const padRight = parseFloat(containerStyle.paddingRight) || 0;
  let parentWidth = Math.max(MIN_WIDTH, containerEl.clientWidth - padLeft - padRight);

  // ─── Calculate scales at full container width ───
  let launch_x = Math.max(60, parentWidth * 0.12);
  let right_margin = 30;
  let drawable_width = parentWidth - launch_x - right_margin;

  let x_scale = drawable_width / (max_x + 10);
  let y_scale = 30;

  let height = (2 + sizeMaxY - sizeMinY) * y_scale + 30 + AXIS_LABEL_HEIGHT + TITLE_HEIGHT;

  // ─── Cap height to 80% of viewport; if capped, shrink width to match ───
  let maxHeight = window.innerHeight * MAX_HEIGHT_RATIO;
  let width = parentWidth;
  if (height > maxHeight) {
    let scale = maxHeight / height;
    y_scale = y_scale * scale;
    height = maxHeight;
    width = Math.max(MIN_WIDTH, parentWidth * scale);
    launch_x = Math.max(60, width * 0.12);
    drawable_width = width - launch_x - right_margin;
    x_scale = drawable_width / (max_x + 10);
  }

  // ─── High-DPI rendering ───
  const dpr = window.devicePixelRatio || 1;
  myCanvas.value.width = width * dpr;
  myCanvas.value.height = height * dpr;
  myCanvas.value.style.width = width + 'px';
  myCanvas.value.style.height = height + 'px';

  const ctx = myCanvas.value.getContext("2d");
  ctx.scale(dpr, dpr);

  // ─── plotBottom excludes the reserved x-axis label band, so tick labels
  // never sit under trajectories/targets regardless of cut angle ───
  let plotBottom = height - AXIS_LABEL_HEIGHT;
  let launch_y = plotBottom - (2 - sizeMinY) * y_scale;
  let h = 1.22 * y_scale;

  return {
    dists, cut, unit, r, width, height, x_scale, y_scale, launch_x, launch_y,
    right_margin, h, plotBottom, drawable_width, labelMinY, labelMaxY, max_x,
  };
}

// The actual drawing, given a layout from sizeCanvas() above — kept as a
// separate step so the per-distance loop at the bottom (each iteration
// re-running findAngle's iterative solver) can yield periodically (see
// async_chunking.js) instead of blocking the main thread for its whole
// duration in one go. Distance counts here are usually small (a handful),
// so this matters far less than it does for full_marks.vue/graph.vue's much
// longer sweeps, but there's no upper bound on how many a user adds.
async function drawContent(layout) {
  if (!layout) return;
  const {
    dists, cut, unit, r, width, height, x_scale, y_scale, launch_x, launch_y,
    right_margin, h, plotBottom, drawable_width, labelMinY, labelMaxY, max_x,
  } = layout;

  const ctx = myCanvas.value.getContext("2d");
  const colors = getCanvasColors();
  const { bg, grid, line, text, accent } = colors;

  // ─── Fill background ───
  ctx.fillStyle = bg;
  ctx.fillRect(0, 0, width, height);

  // ─── Title ───
  ctx.fillStyle = text;
  ctx.textAlign = "center";
  ctx.textBaseline = "middle";
  ctx.font = "bold 14px sans-serif";
  ctx.fillText("Trajectory", width / 2, TITLE_HEIGHT / 2);

  // ─── Tick steps: nice round numbers, spaced so labels don't crowd ───
  let stepY = niceStep(labelMaxY - labelMinY, plotBottom, 32);
  let firstY = Math.ceil(labelMinY / stepY) * stepY;

  let labelMaxX = Math.ceil(max_x / calc.toScale(unit));
  let stepX = niceStep(labelMaxX, drawable_width, 70);

  // ─── Grid ───
  ctx.strokeStyle = grid;
  ctx.lineWidth = 1;
  ctx.beginPath();
  for (let y = firstY; y <= labelMaxY; y += stepY) {
    let py = launch_y - y * calc.toScale(unit) * y_scale;
    if (py < TITLE_HEIGHT || py > plotBottom) continue;
    ctx.moveTo(launch_x, py);
    ctx.lineTo(width - right_margin, py);
  }
  for (let x = 0; x <= labelMaxX; x += stepX) {
    let px = launch_x + x * calc.toScale(unit) * x_scale;
    if (px > width - right_margin) continue;
    ctx.moveTo(px, TITLE_HEIGHT);
    ctx.lineTo(px, plotBottom);
  }
  ctx.stroke();

  ctx.fillStyle = text;
  ctx.strokeStyle = line;
  ctx.lineWidth = 1;

  // ─── Draw bow position — box sits behind the x=0 origin so the
  // trajectory departs from its front (right) edge, not through its side ───
  const bow = new Path2D();
  bow.rect(launch_x - BOW_WIDTH, launch_y - h/2, BOW_WIDTH, h);
  ctx.stroke(bow);

  // ─── Origin label ───
  ctx.textBaseline = "top";
  ctx.textAlign = "center";
  ctx.font = "16px serif";
  ctx.fillText("0", launch_x, launch_y + h/2 + 5);

  // ─── Axis tick font (smaller than the title/origin label) ───
  ctx.font = "12px sans-serif";

  // ─── Y labels ───
  ctx.textAlign = "right";
  ctx.textBaseline = "middle";
  for (let y = firstY; y <= labelMaxY; y += stepY) {
    let py = launch_y - y * calc.toScale(unit) * y_scale;
    if (py < TITLE_HEIGHT || py > plotBottom) continue;
    ctx.fillText(formatTick(y, stepY) + unit, launch_x - 10, py);
  }

  // ─── X labels — always drawn, in their own band below the plot so a
  // cut angle can never push trajectories/target labels into them ───
  ctx.textAlign = "center";
  ctx.textBaseline = "top";
  for (let x = 0; x <= labelMaxX; x += stepX) {
    let px = launch_x + x * calc.toScale(unit) * x_scale;
    if (px > width - right_margin) continue;
    ctx.fillText(formatTick(x, stepX) + unit, px, plotBottom + AXIS_LABEL_PAD);
  }

  // ─── Draw trajectories ───
  ctx.font = "16px serif";
  ctx.textAlign = "center";
  ctx.textBaseline = "top";
  for (let i = 0; i < dists.length; i++) {
    let d = dists[i];
    let { x: x_target, y: y_target } = calc.cutDists(d * calc.toScale(unit), cut);
    if (x_target > max_x) {
      // max_x comes from the largest distance the earlier probe loop
      // managed to solve — anything past it is assumed to fail the same
      // way and is skipped without even attempting drawTrajectory, so
      // without this it would silently never reach the toast below.
      maybeToastDistanceError(d, unit, 'beyond calculable range');
      continue;
    }

    try {
      drawTrajectory(ctx, x_scale, y_scale, launch_x, launch_y, unit, h,
        r.v, r.drag, d, cut, plotBottom);
      lastTrajectoryDistanceErrors.delete(d);
    } catch (error) {
      console.log(`Failed to draw trajectory for ${d}: ${error}`);
      maybeToastDistanceError(d, unit, error.message);
    }

    await yieldToFrame();
  }
}

async function drawTrajectories() {
  const layout = await sizeCanvas();
  await drawContent(layout);
}

// ─── Resize handling ───
// Observe the container's PARENT. Its size changes only on window/layout
// resize — never when the canvas inside changes size. No feedback loop.
let resizeTimer = null;
function onResize() {
  clearTimeout(resizeTimer);
  resizeTimer = setTimeout(() => {
    drawTrajectories();
  }, 150);
}

let resizeObserver = null;

const { currentTheme } = useTheme();

watch([computedResult, input, currentTheme], async () => {
  await nextTick();
  await drawTrajectories()
}, { immediate: false, deep: true })

// Filled in by sizeCanvas() below, in time for the deferred drawContent()
// call further down to reuse it without sizing all over again.
let initialLayout = null;

onMounted(async () => {
  // Sizes the canvas immediately (not deferred — see below) so the
  // panel's own open animation measures the correct final height right
  // away, instead of a bare <canvas>'s default (~150px) that would
  // otherwise sit there until drawContent() finishes and grows it after
  // the fact. Sizing alone is comparatively cheap (a couple of cached
  // calc.maxHeight calls), unlike the actual per-distance drawing below.
  initialLayout = await sizeCanvas();

  const parent = canvasContainer.value?.parentElement;
  if (parent) {
    resizeObserver = new ResizeObserver(onResize);
    resizeObserver.observe(parent);
  }
})

// The initial draw is deferred (see use_deferred_compute.js) so the
// panel's own open animation gets to paint and start before this blocks
// the main thread — a later redraw (the watcher above) isn't deferred
// since the panel is already open by then, with no animation racing it.
// Fast enough now that there's nothing worth a "Computing…" placeholder for.
useDeferredCompute(() => drawContent(initialLayout));

onBeforeUnmount(() => {
  if (resizeObserver) {
    resizeObserver.disconnect();
    resizeObserver = null;
  }
  clearTimeout(resizeTimer);
})
</script>

<template>
  <div class="centered">
    <RecomputeButton />

    <label>Unit:
      <UnitSelect v-model="input.unit" />
    </label>

    <label>Cut angle:
      <input type="number" v-model.number="input.cut_angle" class="input-measure" />
    </label>

    <div class="distances-row">
      <span>Distances:</span>
      <div class="distance-chip" v-for="(d, index) in input.distances" :key="index">
        <input type="number" class="input-measure" v-model.number="input.distances[index]" />
        <button
          class="btn-delete"
          @click="removeDistance(index)"
          title="Remove distance"
          aria-label="Remove distance"
          :disabled="input.distances.length <= 1" >
          ✕
        </button>
      </div>
      <button class="btn-add" @click="addDistance" title="Add distance">
        + Add
      </button>
    </div>
  </div>
  <div id="trajectory_canvas_container" ref="canvasContainer" >
    <canvas ref="myCanvas"></canvas>
  </div>

</template>

<style scoped>

.distances-row {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  justify-content: center;
  gap: var(--space-xs);
}

.distance-chip {
  display: flex;
  align-items: center;
  gap: 2px;
}

.distances-row .btn-add {
  margin-top: 0;
  padding: 0.2rem 0.5rem;
}

#trajectory_canvas_container {
  width: 100%;
  max-width: 100%;
  min-width: 0;              /* <-- THE FIX: allow flex item to shrink below content size */
  padding: var(--space-sm);
  box-sizing: border-box;
  display: flex;
  justify-content: center;
  flex-direction: column;
  align-items: center;
  border: 3px solid var(--color-border);
  overflow: hidden;
}

</style>

